import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FiRefreshCcw } from "react-icons/fi";

import { useDispatch, useSelector } from "react-redux";

import useSwal from "../../../../../hooks/useSwal";
import { ORDER_STATUS_CARDS } from "./utils/orderConstants";
import {
  acceptOrder,
  applyOrderFilters,
  cancelOrder,
  clearSelectedOrder,
  fetchOrderById,
  fetchOrders,
  resetOrderFilters,
  returnOrder,
  selectAppliedOrderFilters,
  selectOrderActionState,
  selectOrderFilters,
  selectOrderLoading,
  selectOrderManagement,
  selectOrderPagination,
  selectOrderSummary,
  selectOrders,
  selectSelectedOrder,
  setOrderDateRange,
  setOrderFilter,
  setOrderLimit,
  setOrderPage,
  setOrderStatusFilter,
  shipOrder,
  updateOrderNotes,
  updateOrderStatus,
  updateOrderTracking,
} from "../../../../../store/orderManagement/orderManagementSlice";

import OrderSummaryCards from "./components/OrderSummaryCards";
import OrderDateFilters from "./components/OrderDateFilters";
import OrderFilters from "./components/OrderFilters";
import OrderTable from "./components/OrderTable";
import OrderDetailsDrawer from "./components/OrderDetailsDrawer";
import ShippingDialog from "./components/ShippingDialog";
import CancelOrderDialog from "./components/CancelOrderDialog";
import ReturnOrderDialog from "./components/ReturnOrderDialog";
import OrderNotes from "./components/OrderNotes";

import { getDateRangeForPreset } from "./utils/orderDateUtils";

import "./OrderManagement.css";

/* =========================================================
   COMPONENT
========================================================= */

const OrderManagement = () => {
  const dispatch = useDispatch();

  const swal = useSwal();

  /* =======================================================
     REDUX
  ======================================================= */

  const orders = useSelector(selectOrders);

  const summary = useSelector(selectOrderSummary);

  const filters = useSelector(selectOrderFilters);

  const appliedFilters = useSelector(selectAppliedOrderFilters);

  const pagination = useSelector(selectOrderPagination);

  const loading = useSelector(selectOrderLoading);

  const selectedOrder = useSelector(selectSelectedOrder);

  const orderState = useSelector(selectOrderManagement);

  const actionState = useSelector(selectOrderActionState);

  /* =======================================================
     LOCAL UI STATE
  ======================================================= */

  const [detailsOpen, setDetailsOpen] = useState(false);

  const [shippingOpen, setShippingOpen] = useState(false);

  const [shippingMode, setShippingMode] = useState("ship");

  const [shippingOrder, setShippingOrder] = useState(null);

  const [cancelOpen, setCancelOpen] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);

  const [returnOpen, setReturnOpen] = useState(false);

  const [returnTarget, setReturnTarget] = useState(null);

  const [notesOpen, setNotesOpen] = useState(false);

  const [notesTarget, setNotesTarget] = useState(null);

  /* =======================================================
     REQUEST PARAMS
  ======================================================= */

  const requestParams = useMemo(
    () => ({
      page: pagination.page,

      limit: pagination.limit,

      search: appliedFilters.search,

      status: appliedFilters.status,

      paymentStatus: appliedFilters.paymentStatus,

      paymentMethod: appliedFilters.paymentMethod,

      dateFrom: appliedFilters.dateFrom,

      dateTo: appliedFilters.dateTo,
    }),
    [pagination.page, pagination.limit, appliedFilters],
  );

  /* =======================================================
     LOAD ORDERS
  ======================================================= */

  const loadOrders = useCallback(async () => {
    await dispatch(fetchOrders(requestParams));
  }, [dispatch, requestParams]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  /* =======================================================
     DATE PRESET
  ======================================================= */

  const handleDatePreset = (preset) => {
    if (preset === "custom") {
      dispatch(
        setOrderFilter({
          key: "datePreset",

          value: "custom",
        }),
      );

      return;
    }

    const range = getDateRangeForPreset(preset);

    dispatch(
      setOrderDateRange({
        preset,

        dateFrom: range.dateFrom,

        dateTo: range.dateTo,
      }),
    );
  };

  /* =======================================================
     CUSTOM DATE FIELD
  ======================================================= */

  const handleDateChange = (key, value) => {
    dispatch(
      setOrderFilter({
        key,
        value,
      }),
    );
  };

  /* =======================================================
     APPLY CUSTOM DATE
  ======================================================= */

  const handleApplyCustomDate = () => {
    if (
      filters.dateFrom &&
      filters.dateTo &&
      filters.dateFrom > filters.dateTo
    ) {
      swal.error({
        title: "Invalid Date Range",

        text: "From date cannot be after To date.",
      });

      return;
    }

    dispatch(applyOrderFilters());
  };

  /* =======================================================
     FILTER CHANGE
  ======================================================= */

  const handleFilterChange = (key, value) => {
    dispatch(
      setOrderFilter({
        key,
        value,
      }),
    );
  };

  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch = () => {
    dispatch(applyOrderFilters());
  };

  /* =======================================================
     RESET
  ======================================================= */

  const handleReset = () => {
    dispatch(resetOrderFilters());

    const range = getDateRangeForPreset("this_month");

    dispatch(
      setOrderDateRange({
        preset: "this_month",

        dateFrom: range.dateFrom,

        dateTo: range.dateTo,
      }),
    );
  };

  /* =======================================================
     STATUS CARD FILTER
  ======================================================= */

  const handleStatusFilter = (status) => {
    dispatch(setOrderStatusFilter(status));
  };

  /* =======================================================
     VIEW ORDER
  ======================================================= */

  const handleViewOrder = async (order) => {
    if (!order?._id) {
      return;
    }

    setDetailsOpen(true);

    const result = await dispatch(fetchOrderById(order._id));

    if (fetchOrderById.rejected.match(result)) {
      setDetailsOpen(false);

      await swal.error({
        title: "Unable to Load Order",

        text: result.payload || "Unable to fetch order details.",
      });
    }
  };

  /* =======================================================
     CLOSE DETAILS
  ======================================================= */

  const handleCloseDetails = () => {
    setDetailsOpen(false);

    dispatch(clearSelectedOrder());
  };

  /* =======================================================
     REFRESH SELECTED ORDER
  ======================================================= */

  const refreshSelectedOrder = async (orderId) => {
    if (!orderId) {
      return;
    }

    await dispatch(fetchOrderById(orderId));
  };

  /* =======================================================
     ACCEPT ORDER
  ======================================================= */

  const handleAcceptOrder = async (order) => {
    if (!order?._id) {
      return;
    }

    const confirmation = await swal.confirm({
      icon: "question",

      title: "Accept Order?",

      text: `Accept order ${order.orderNumber || ""}?`,

      showCancelButton: true,

      confirmButtonText: "Accept Order",

      cancelButtonText: "Cancel",
    });

    if (!confirmation?.isConfirmed) {
      return;
    }

    const result = await dispatch(
      acceptOrder({
        orderId: order._id,
      }),
    );

    if (acceptOrder.fulfilled.match(result)) {
      await swal.success({
        title: "Order Accepted",

        text: result.payload?.message || "Order accepted successfully.",

        timer: 1400,

        showConfirmButton: false,
      });

      await loadOrders();

      if (detailsOpen) {
        await refreshSelectedOrder(order._id);
      }
    } else {
      await swal.error({
        title: "Unable to Accept Order",

        text: result.payload || "Unable to accept order.",
      });
    }
  };

  /* =======================================================
     CHANGE STATUS
  ======================================================= */

  const handleChangeStatus = async (order, status) => {
    if (!order?._id || !status) {
      return;
    }

    const readableStatus = String(status)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());

    const confirmation = await swal.confirm({
      icon: "question",

      title: "Update Order Status?",

      text: `Change ${order.orderNumber || "this order"} to ${readableStatus}?`,

      showCancelButton: true,

      confirmButtonText: readableStatus,

      cancelButtonText: "Cancel",
    });

    if (!confirmation?.isConfirmed) {
      return;
    }

    const result = await dispatch(
      updateOrderStatus({
        orderId: order._id,

        status,
      }),
    );

    if (updateOrderStatus.fulfilled.match(result)) {
      await swal.success({
        title: "Order Updated",

        text: result.payload?.message || "Order status updated successfully.",

        timer: 1400,

        showConfirmButton: false,
      });

      await loadOrders();

      if (detailsOpen) {
        await refreshSelectedOrder(order._id);
      }
    } else {
      await swal.error({
        title: "Status Update Failed",

        text: result.payload || "Unable to update order status.",
      });
    }
  };

  /* =======================================================
     PRIMARY TABLE ACTION
  ======================================================= */

  const handlePrimaryAction = (order, action) => {
    if (!action) {
      return;
    }

    if (action.action === "accept") {
      handleAcceptOrder(order);

      return;
    }

    if (action.action === "ship") {
      handleOpenShip(order);

      return;
    }

    handleChangeStatus(order, action.action);
  };

  /* =======================================================
     OPEN SHIP
  ======================================================= */

  const handleOpenShip = (order) => {
    if (!order?._id) {
      return;
    }

    setShippingMode("ship");

    setShippingOrder(order);

    setShippingOpen(true);
  };

  /* =======================================================
     OPEN TRACKING EDIT
  ======================================================= */

  const handleOpenTracking = (order) => {
    if (!order?._id) {
      return;
    }

    setShippingMode(order.status === "packed" ? "ship" : "tracking");

    setShippingOrder(order);

    setShippingOpen(true);
  };

  /* =======================================================
     CLOSE SHIPPING
  ======================================================= */

  const handleCloseShipping = () => {
    if (actionState.loading) {
      return;
    }

    setShippingOpen(false);

    setShippingOrder(null);

    setShippingMode("ship");
  };

  /* =======================================================
     SUBMIT SHIPPING / TRACKING
  ======================================================= */

  const handleShippingSubmit = async (values) => {
    if (!shippingOrder?._id) {
      return;
    }

    let result;

    if (shippingMode === "ship") {
      result = await dispatch(
        shipOrder({
          orderId: shippingOrder._id,

          ...values,
        }),
      );

      if (shipOrder.fulfilled.match(result)) {
        await swal.success({
          title: "Order Shipped",

          text: result.payload?.message || "Order shipped successfully.",

          timer: 1500,

          showConfirmButton: false,
        });
      }
    } else {
      result = await dispatch(
        updateOrderTracking({
          orderId: shippingOrder._id,

          courierName: values.courierName,

          trackingNumber: values.trackingNumber,

          trackingUrl: values.trackingUrl,

          estimatedDeliveryDate: values.estimatedDeliveryDate,

          shippingNote: values.shippingNote,
        }),
      );

      if (updateOrderTracking.fulfilled.match(result)) {
        await swal.success({
          title: "Tracking Updated",

          text:
            result.payload?.message || "Tracking details updated successfully.",

          timer: 1400,

          showConfirmButton: false,
        });
      }
    }

    const succeeded =
      shippingMode === "ship"
        ? shipOrder.fulfilled.match(result)
        : updateOrderTracking.fulfilled.match(result);

    if (succeeded) {
      const orderId = shippingOrder._id;

      setShippingOpen(false);

      setShippingOrder(null);

      await loadOrders();

      if (detailsOpen) {
        await refreshSelectedOrder(orderId);
      }

      return;
    }

    await swal.error({
      title:
        shippingMode === "ship"
          ? "Unable to Ship Order"
          : "Unable to Update Tracking",

      text: result?.payload || "Unable to save shipping information.",
    });
  };

  /* =======================================================
     OPEN CANCEL
  ======================================================= */

  const handleOpenCancel = (order) => {
    if (!order?._id) {
      return;
    }

    setCancelTarget(order);

    setCancelOpen(true);
  };

  /* =======================================================
     CLOSE CANCEL
  ======================================================= */

  const handleCloseCancel = () => {
    if (actionState.loading) {
      return;
    }

    setCancelOpen(false);

    setCancelTarget(null);
  };

  /* =======================================================
     CANCEL ORDER
  ======================================================= */

  const handleCancelSubmit = async (values) => {
    if (!cancelTarget?._id) {
      return;
    }

    const orderId = cancelTarget._id;

    const result = await dispatch(
      cancelOrder({
        orderId,

        reason: values.reason,

        note: values.note,
      }),
    );

    if (cancelOrder.fulfilled.match(result)) {
      setCancelOpen(false);

      setCancelTarget(null);

      let message = result.payload?.message || "Order cancelled successfully.";

      if (result.payload?.refundRequired) {
        message += " Payment refund is still required.";
      }

      await swal.success({
        title: "Order Cancelled",

        text: message,

        timer: result.payload?.refundRequired ? undefined : 1700,

        showConfirmButton: Boolean(result.payload?.refundRequired),
      });

      await loadOrders();

      if (detailsOpen) {
        await refreshSelectedOrder(orderId);
      }

      return;
    }

    await swal.error({
      title: "Cancellation Failed",

      text: result.payload || "Unable to cancel order.",
    });
  };

  /* =======================================================
     OPEN RETURN
  ======================================================= */

  const handleOpenReturn = (order) => {
    if (!order?._id) {
      return;
    }

    setReturnTarget(order);

    setReturnOpen(true);
  };

  /* =======================================================
     CLOSE RETURN
  ======================================================= */

  const handleCloseReturn = () => {
    if (actionState.loading) {
      return;
    }

    setReturnOpen(false);

    setReturnTarget(null);
  };

  /* =======================================================
     RETURN ORDER
  ======================================================= */

  const handleReturnSubmit = async (values) => {
    if (!returnTarget?._id) {
      return;
    }

    const orderId = returnTarget._id;

    const result = await dispatch(
      returnOrder({
        orderId,

        reason: values.reason,

        note: values.note,
      }),
    );

    if (returnOrder.fulfilled.match(result)) {
      setReturnOpen(false);

      setReturnTarget(null);

      let message = result.payload?.message || "Order returned successfully.";

      if (result.payload?.refundRequired) {
        message += " Payment refund is still required.";
      }

      await swal.success({
        title: "Order Returned",

        text: message,

        timer: result.payload?.refundRequired ? undefined : 1700,

        showConfirmButton: Boolean(result.payload?.refundRequired),
      });

      await loadOrders();

      if (detailsOpen) {
        await refreshSelectedOrder(orderId);
      }

      return;
    }

    await swal.error({
      title: "Return Failed",

      text: result.payload || "Unable to return order.",
    });
  };

  /* =======================================================
     OPEN NOTES
  ======================================================= */

  const handleOpenNotes = (order) => {
    if (!order?._id) {
      return;
    }

    setNotesTarget(order);

    setNotesOpen(true);
  };

  /* =======================================================
     CLOSE NOTES
  ======================================================= */

  const handleCloseNotes = () => {
    if (actionState.loading) {
      return;
    }

    setNotesOpen(false);

    setNotesTarget(null);
  };

  /* =======================================================
     UPDATE NOTES
  ======================================================= */

  const handleNotesSubmit = async (values) => {
    if (!notesTarget?._id) {
      return;
    }

    const orderId = notesTarget._id;

    const result = await dispatch(
      updateOrderNotes({
        orderId,

        notes: values.notes,
      }),
    );

    if (updateOrderNotes.fulfilled.match(result)) {
      setNotesOpen(false);

      setNotesTarget(null);

      await swal.success({
        title: "Notes Saved",

        text: result.payload?.message || "Order notes updated successfully.",

        timer: 1300,

        showConfirmButton: false,
      });

      await loadOrders();

      if (detailsOpen) {
        await refreshSelectedOrder(orderId);
      }

      return;
    }

    await swal.error({
      title: "Unable to Save Notes",

      text: result.payload || "Unable to update order notes.",
    });
  };

  /* =======================================================
     PAGE
  ======================================================= */

  const handlePageChange = (page) => {
    if (!Number.isInteger(page) || page < 1 || page > pagination.totalPages) {
      return;
    }

    dispatch(setOrderPage(page));
  };

  /* =======================================================
     LIMIT
  ======================================================= */

  const handleLimitChange = (limit) => {
    dispatch(setOrderLimit(limit));
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="order-management-page">
      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="order-management-header">
        <div>
          <span className="order-management-heading-label">
            CUSTOMER ORDERS
          </span>

          <h2>Order Management</h2>

          <p>
            Monitor orders from placement through payment, fulfillment,
            shipping, delivery, cancellation and return.
          </p>
        </div>

        <button
          type="button"
          className="order-management-refresh"
          onClick={loadOrders}
          disabled={loading}
        >
          <FiRefreshCcw className={loading ? "spinning" : ""} />
          Refresh
        </button>
      </div>

      {/* ===================================================
          DATE FILTER
      =================================================== */}

      <OrderDateFilters
        filters={filters}
        onPresetChange={handleDatePreset}
        onDateChange={handleDateChange}
        onApplyCustomDate={handleApplyCustomDate}
      />

      {/* ===================================================
          SUMMARY
      =================================================== */}

      <OrderSummaryCards summary={summary} />

      {/* ===================================================
          STATUS CARDS
      =================================================== */}

<div className="order-status-card-grid">
  {ORDER_STATUS_CARDS.map(
    (item) => {
      const Icon =
        item.icon;

      const count =
        item.key
          ? Number(
              summary
                ?.statuses
                ?.[item.key] ||
              0
            )
          : Number(
              summary
                ?.totalOrders ||
              0
            );


      const active =
        appliedFilters
          ?.status ===
        item.key;


      return (
        <button
          key={
            item.key ||
            "all"
          }
          type="button"
          className={`order-status-card ${
            active
              ? "active"
              : ""
          }`}
          onClick={() =>
            handleStatusFilter(
              item.key
            )
          }
        >
          <span>
            {Icon ? (
              <Icon />
            ) : null}
          </span>


          <div>
            <small>
              {item.label}
            </small>

            <strong>
              {count}
            </strong>
          </div>
        </button>
      );
    }
  )}
</div>

      {/* ===================================================
          FILTERS
      =================================================== */}

      <OrderFilters
        filters={filters}
        loading={loading}
        onChange={handleFilterChange}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {/* ===================================================
          ERROR
      =================================================== */}

      {orderState?.error ? (
        <div className="order-management-error">{orderState.error}</div>
      ) : null}

      {/* ===================================================
          TABLE
      =================================================== */}

      <OrderTable
        orders={orders}
        loading={loading}
        pagination={pagination}
        actionState={actionState}
        onView={handleViewOrder}
        onPrimaryAction={handlePrimaryAction}
        onShip={handleOpenShip}
        onCancel={handleOpenCancel}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* ===================================================
          DETAILS DRAWER
      =================================================== */}

      <OrderDetailsDrawer
        open={detailsOpen}
        order={selectedOrder}
        loading={orderState?.detailsLoading}
        actionLoading={actionState.loading}
        onClose={handleCloseDetails}
        onAccept={handleAcceptOrder}
        onChangeStatus={handleChangeStatus}
        onShip={handleOpenShip}
        onCancel={handleOpenCancel}
        onReturn={handleOpenReturn}
        onEditTracking={handleOpenTracking}
        onEditNotes={handleOpenNotes}
      />

      {/* ===================================================
          SHIPPING / TRACKING
      =================================================== */}

      <ShippingDialog
        open={shippingOpen}
        order={shippingOrder}
        mode={shippingMode}
        loading={actionState.loading}
        onClose={handleCloseShipping}
        onSubmit={handleShippingSubmit}
      />

      {/* ===================================================
          CANCEL
      =================================================== */}

      <CancelOrderDialog
        open={cancelOpen}
        order={cancelTarget}
        loading={actionState.loading}
        onClose={handleCloseCancel}
        onSubmit={handleCancelSubmit}
      />

      {/* ===================================================
          RETURN
      =================================================== */}

      <ReturnOrderDialog
        open={returnOpen}
        order={returnTarget}
        loading={actionState.loading}
        onClose={handleCloseReturn}
        onSubmit={handleReturnSubmit}
      />

      {/* ===================================================
          NOTES
      =================================================== */}

      <OrderNotes
        open={notesOpen}
        order={notesTarget}
        loading={actionState.loading}
        onClose={handleCloseNotes}
        onSubmit={handleNotesSubmit}
      />
    </div>
  );
};

export default OrderManagement;
